import { create } from 'zustand';
import { immer } from 'zustand/middleware/immer';
import {
  BatchKanbanViewBase,
  KanbanProductionQueue,
  KanbanTaskView,
  KanbanTaskViewBase,
  KanbanUpdate,
  TaskStatus,
} from '@nexelem/graphne-api';
import { UniqueIdentifier } from '@dnd-kit/core';
import { KanbanObjectUtils, getExternalId } from '../utils';
import { createSelector } from 'reselect';

export type KanbanEventState = {
  data?: KanbanUpdate;
  isLoading: boolean;
  timeoutId: NodeJS.Timeout | null;
  setData: (data: KanbanUpdate) => void;
  setIsKanbanLoading: (isKanbanLoading: boolean) => void;
  reset: () => void;
};

export const useKanbanEventStore = create(
  immer<KanbanEventState>((set) => {
    return {
      isLoading: true,
      timeoutId: null,
      setIsKanbanLoading: (isKanbanLoading) =>
        set((state) => {
          state.isLoading = isKanbanLoading;
        }),
      setData: (data) =>
        set((state) => {
          state.isLoading = false;
          state.data = data;

          if (state.timeoutId) {
            clearTimeout(state.timeoutId);
          }

          // Clear data after 3 minutes, to not show old data after a while
          const timeoutId = setTimeout(
            () => {
              set((state) => {
                state.isLoading = true;
                state.data = undefined;
              });
            },
            3 * 60 * 1000,
          );

          state.timeoutId = timeoutId;
        }),
      reset: () => {
        set((state) => {
          state.isLoading = true;
          state.data = undefined;
          state.timeoutId = null;
        });
      },
    };
  }),
);

const EMPTY_ARRAY: unknown[] = [];

const selectAllTasks = createSelector(
  (state: KanbanEventState) => state.data?.productionQueues || (EMPTY_ARRAY as KanbanProductionQueue[]),
  (queues) => queues.map(({ tasks }) => tasks).flat(),
);

export const selectProductionQueues = (state: KanbanEventState) =>
  state.data?.productionQueues || (EMPTY_ARRAY as KanbanProductionQueue[]);

export const selectTaskCountByStatus = (status: TaskStatus) =>
  createSelector(
    selectAllTasks,
    (tasks) => tasks.filter((task) => KanbanObjectUtils.getTaskStatus(task) === status).length,
  );

export const selectQueueTasksIds = (queueId?: number) => (state: KanbanEventState) => {
  const tasks = state.data?.productionQueues
    ?.find(({ queue }) => queue.id === queueId)
    ?.tasks?.filter((t): t is KanbanTaskView & { tpe: 'Task' } => t?.tpe === 'Task');

  return {
    [TaskStatus.Pending]: tasks?.filter(({ status }) => status === TaskStatus.Pending).map(({ id }) => id),
    [TaskStatus.InProgress]: tasks?.filter(({ status }) => status === TaskStatus.InProgress).map(({ id }) => id),
    [TaskStatus.Completed]: tasks?.filter(({ status }) => status === TaskStatus.Completed).map(({ id }) => id),
  };
};

export const selectTaskById = (id: number) =>
  createSelector(selectAllTasks, (tasks) => tasks.find((task) => KanbanObjectUtils.getTaskId(task) === id));

/**
 * @deprecated replace with selectTaskById, require add support for batch to dnd
 * */
export const selectOnlyTaskById = (id: number) =>
  createSelector(selectAllTasks, (tasks) =>
    tasks.filter((t): t is KanbanTaskViewBase & { tpe: 'Task' } => t?.tpe === 'Task').find((task) => task.id === id),
  );

export const selectBatchById = (id: number) =>
  createSelector(selectAllTasks, (tasks) =>
    tasks
      .filter((t): t is BatchKanbanViewBase & { tpe: 'Batch' } => t?.tpe === 'Batch')
      .find((task) => task.batchId === id),
  );

export const selectQueueNameById = (id?: number) => (state: KanbanEventState) =>
  state.data?.productionQueues?.find(({ queue }) => id && queue.id === id)?.queue.name;

export const selectTaskStatusById = (id?: UniqueIdentifier) => (state: KanbanEventState) => {
  if (!id) {
    return;
  }

  return state.data?.productionQueues
    ?.map(({ tasks }) => tasks)
    .flat()
    .filter((t): t is KanbanTaskViewBase & { tpe: 'Task' } => t?.tpe === 'Task')
    .find((task) => task?.id === id)?.status;
};

export const selectExternalQueueId = (queueId?: number) => (state: KanbanEventState) => {
  if (!queueId) {
    return;
  }

  const productionQueue = state.data?.productionQueues?.find(({ queue }) => queue.id === queueId);

  return getExternalId(productionQueue?.queue);
};

export const selectTaskTypeById = (id: number) => (state: KanbanEventState) =>
  state.data?.productionQueues
    ?.map(({ tasks }) => tasks)
    .flat()
    .find((task) => KanbanObjectUtils.getTaskId(task) === id)?.tpe;
